import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CreatePortfolioRequest,
  Portfolio,
  portfolioApi,
} from "../../services/portfolioApi";

interface PortfolioSidebarProps {
  onPortfolioSelect?: (portfolio: Portfolio) => void;
}

const PortfolioSidebar: React.FC<PortfolioSidebarProps> = ({
  onPortfolioSelect,
}) => {
  const navigate = useNavigate();
  const { portfolioId } = useParams<{ portfolioId: string }>();

  // Initialize portfolios from sessionStorage if available
  const [portfolios, setPortfolios] = useState<Portfolio[]>(() => {
    try {
      const cached = sessionStorage.getItem("portfolios_cache");
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState("");
  const [newPortfolioDescription, setNewPortfolioDescription] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    // Only load if we don't have cached portfolios
    if (portfolios.length === 0) {
      loadPortfolios();
    }
  }, []);

  const loadPortfolios = async (forceReload = false) => {
    try {
      // Only show loading state if we don't have portfolios yet or forcing reload
      if (portfolios.length === 0 || forceReload) {
        setLoading(true);
      }
      const response = await portfolioApi.getPortfolios();
      setPortfolios(response.results);
      // Cache portfolios in sessionStorage
      try {
        sessionStorage.setItem(
          "portfolios_cache",
          JSON.stringify(response.results)
        );
      } catch (error) {
        return;
      }
    } catch (error) {
      return;
    } finally {
      setLoading(false);
    }
  };

  const handlePortfolioClick = (portfolio: Portfolio) => {
    navigate(`/portfolio?portfolio_id=${portfolio.id}`);
    onPortfolioSelect?.(portfolio);
  };

  const handleBackToList = () => {
    navigate("/portfolios");
  };

  const handleCreatePortfolio = async () => {
    if (!newPortfolioName.trim()) return;

    try {
      setCreating(true);
      const data: CreatePortfolioRequest = {
        name: newPortfolioName.trim(),
        description: newPortfolioDescription.trim() || undefined,
      };

      const newPortfolio = await portfolioApi.createPortfolio(data);
      const updatedPortfolios = [newPortfolio, ...portfolios];
      setPortfolios(updatedPortfolios);
      // Update cache
      try {
        sessionStorage.setItem(
          "portfolios_cache",
          JSON.stringify(updatedPortfolios)
        );
      } catch (error) {
        return;
      }
      setCreateDialogOpen(false);
      setNewPortfolioName("");
      setNewPortfolioDescription("");

      // Navigate to the new portfolio
      navigate(`/portfolio?portfolio_id=${newPortfolio.id}`);
      onPortfolioSelect?.(newPortfolio);
    } catch (error) {
      return;
    } finally {
      setCreating(false);
    }
  };

  const entityTypes = [
    { key: "organization" as const, label: "Orgs", color: "#1976d2" },
    { key: "facility" as const, label: "Facilities", color: "#388e3c" },
    { key: "project" as const, label: "Projects", color: "#f57c00" },
    { key: "source" as const, label: "Sources", color: "#7b1fa2" },
  ];

  return (
    <>
      <Box
        sx={{
          width: 320,
          height: "100%",
          backgroundColor: "background.paper",
          borderRight: 1,
          borderColor: "divider",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: "divider" }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1.5,
            }}
          >
            <Typography variant="h6" sx={{ color: "#fff" }}>
              Portfolios
            </Typography>
            {portfolioId && (
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToList}
                sx={{
                  color: "#aaa",
                  minWidth: "auto",
                  px: 1,
                  "&:hover": {
                    color: "#fff",
                    backgroundColor: "#333",
                  },
                }}
              >
                Back
              </Button>
            )}
          </Box>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              borderColor: "#555",
              color: "#fff",
              "&:hover": {
                borderColor: "#777",
                backgroundColor: "#333",
              },
            }}
          >
            Create Portfolio
          </Button>
        </Box>

        <List sx={{ p: 0 }}>
          {loading
            ? // Loading skeleton
              Array.from({ length: 5 }).map((_, index) => (
                <ListItem key={index} sx={{ px: 2, py: 1 }}>
                  <Box sx={{ width: "100%" }}>
                    <Skeleton variant="text" width="80%" height={24} />
                    <Skeleton
                      variant="text"
                      width="60%"
                      height={16}
                      sx={{ mt: 0.5 }}
                    />
                    <Box sx={{ display: "flex", gap: 0.5, mt: 1 }}>
                      <Skeleton variant="rectangular" width={60} height={20} />
                      <Skeleton variant="rectangular" width={40} height={20} />
                    </Box>
                  </Box>
                </ListItem>
              ))
            : portfolios.map((portfolio, index) => (
                <React.Fragment key={portfolio.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      selected={portfolio.id === portfolioId}
                      onClick={() => handlePortfolioClick(portfolio)}
                      sx={{
                        px: 1.5,
                        py: 1,
                        "&.Mui-selected": {
                          backgroundColor: "#1976d2",
                          "&:hover": {
                            backgroundColor: "#1565c0",
                          },
                        },
                        "&:hover": {
                          backgroundColor: "#333",
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                color:
                                  portfolio.id === portfolioId
                                    ? "#fff"
                                    : "#fff",
                                fontWeight:
                                  portfolio.id === portfolioId ? 600 : 400,
                              }}
                            >
                              {portfolio.name}
                            </Typography>
                            {/* {portfolio.description && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: portfolio.id === portfolioId ? '#e3f2fd' : '#aaa',
                                mt: 0.5,
                                fontSize: '0.75rem'
                              }}
                            >
                              {portfolio.description}
                            </Typography>
                          )} */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 0.5,
                                mt: 0.75,
                                flexWrap: "wrap",
                              }}
                            >
                              {(() => {
                                const entityChips = entityTypes
                                  .map((entityType) => {
                                    const count =
                                      portfolio.entity_counts[entityType.key] ||
                                      0;
                                    if (count === 0) return null;

                                    return (
                                      <Chip
                                        key={entityType.key}
                                        label={`${count} ${entityType.label}`}
                                        size="small"
                                        sx={{
                                          height: 18,
                                          fontSize: "0.65rem",
                                          backgroundColor:
                                            portfolio.id === portfolioId
                                              ? entityType.color
                                              : entityType.color,
                                          color: "white",
                                          fontWeight: "bold",
                                          opacity:
                                            portfolio.id === portfolioId
                                              ? 1
                                              : 0.8,
                                          "& .MuiChip-label": {
                                            px: 0.5,
                                          },
                                        }}
                                      />
                                    );
                                  })
                                  .filter(Boolean);

                                // Show "No Entities" chip if portfolio is empty
                                if (entityChips.length === 0) {
                                  return (
                                    <Chip
                                      label="No Entities"
                                      size="small"
                                      sx={{
                                        height: 18,
                                        fontSize: "0.65rem",
                                        backgroundColor: "#666",
                                        color: "white",
                                        fontWeight: "normal",
                                        opacity: 0.7,
                                        "& .MuiChip-label": {
                                          px: 0.5,
                                        },
                                      }}
                                    />
                                  );
                                }

                                return entityChips;
                              })()}
                              {/* {portfolio.user_permission && (
                              <Chip
                                label={portfolio.user_permission}
                                size="small"
                                color={getPermissionColor(portfolio.user_permission) as any}
                                variant="outlined"
                                sx={{
                                  height: 18,
                                  fontSize: '0.65rem',
                                  borderColor: portfolio.id === portfolioId ? '#fff' : undefined,
                                  color: portfolio.id === portfolioId ? '#fff' : undefined
                                }}
                              />
                            )} */}
                            </Box>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {/* Add divider between portfolios, but not after the last one */}
                  {index < portfolios.length - 1 && (
                    <Divider
                      sx={{
                        borderColor: "divider",
                        mx: 1.5,
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
        </List>
      </Box>

      {/* Create Portfolio Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "background.raised",
            color: "#fff",
          },
        }}
      >
        <DialogTitle>Create New Portfolio</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Portfolio Name"
            value={newPortfolioName}
            onChange={(e) => setNewPortfolioName(e.target.value)}
            margin="normal"
            required
            autoFocus
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": {
                  borderColor: "#555",
                },
                "&:hover fieldset": {
                  borderColor: "#777",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#aaa",
                "&.Mui-focused": {
                  color: "#1976d2",
                },
              },
            }}
          />
          <TextField
            fullWidth
            label="Description (optional)"
            value={newPortfolioDescription}
            onChange={(e) => setNewPortfolioDescription(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            sx={{
              "& .MuiOutlinedInput-root": {
                color: "#fff",
                "& fieldset": {
                  borderColor: "#555",
                },
                "&:hover fieldset": {
                  borderColor: "#777",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976d2",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#aaa",
                "&.Mui-focused": {
                  color: "#1976d2",
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{ color: "#aaa" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreatePortfolio}
            variant="contained"
            disabled={!newPortfolioName.trim() || creating}
            startIcon={creating ? <CircularProgress size={16} /> : null}
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PortfolioSidebar;
